/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useParams } from 'react-router';
import {
  buildSilentLoginUrl,
  getCurrentUrlLocation,
  hasRecentSilentLoginAttempt,
  isSilentLoginEnabled,
  isSilentLoginSuppressed,
  useAuthenticationContext,
} from '@opengeoweb/authentication';
import type { ConfigType } from '@opengeoweb/shared';
import { browserNavigation, sessionStorageProvider } from '@opengeoweb/shared';
import { WorkspaceLayout } from './workspace/WorkspaceLayout';

interface WorkspaceProps {
  config?: ConfigType;
}

const Workspace: React.FC<WorkspaceProps> = ({ config }: WorkspaceProps) => {
  const { auth, authConfig, onSetAuth, isLoggedIn } =
    useAuthenticationContext();
  const params = useParams();

  const hasAuthenticated =
    sessionStorageProvider.getHasAuthenticated() === 'true';

  // Only relevant for IdPs that don't require an interactive consent screen
  // and honour prompt=none. Skipped after an explicit logout.
  const shouldAttemptSilentLogin =
    isSilentLoginEnabled(config) &&
    !auth &&
    !isLoggedIn &&
    !hasRecentSilentLoginAttempt() &&
    !isSilentLoginSuppressed();

  if (
    shouldAttemptSilentLogin ||
    (config && !auth && !isLoggedIn && hasAuthenticated) ||
    (config?.GW_FEATURE_FORCE_AUTHENTICATION && !hasAuthenticated) ||
    (config?.GW_FEATURE_FORCE_AUTHENTICATION && !isLoggedIn)
  ) {
    const callbackUrl = getCurrentUrlLocation(
      window.location.href,
      config?.GW_APP_URL!,
    );
    sessionStorageProvider.setCallbackUrl(callbackUrl);

    if (shouldAttemptSilentLogin) {
      browserNavigation.assign(
        buildSilentLoginUrl(authConfig.GW_AUTH_LOGIN_URL),
      );
    } else {
      browserNavigation.assign('/login');
    }
  }

  return (
    <WorkspaceLayout
      auth={auth!}
      config={config}
      onSetAuth={onSetAuth}
      workspaceId={params.workspaceId}
    />
  );
};

export default Workspace;
